import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Row, Col, Card, Tag, Typography, Space, Badge, Spin, Empty, Button, message } from 'antd';
import {
    RobotOutlined, WifiOutlined, ThunderboltOutlined,
    AimOutlined, ReloadOutlined, SendOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { ENDPOINTS } from '../../helpers/api/apiConfig';
import { isVehicleDevice, type DeviceItem } from './missionModels';
import type { DeviceCommandItem } from './operationModels';
import { commandStatusColor } from './operationModels';
import { getBatteryPercent, getModuleRuntimeRows, getStatusSnapshot, getUnitStatus } from './deviceStatusSnapshot';

const { Text } = Typography;

interface DeviceFleetPanelProps {
    accessToken?: string;
}

interface DeviceWithFleet extends DeviceItem {
    pendingCommands?: number;
    latestCommand?: DeviceCommandItem | null;
}

const DeviceFleetPanel: React.FC<DeviceFleetPanelProps> = ({ accessToken }) => {
    const navigate = useNavigate();
    const [devices, setDevices] = useState<DeviceWithFleet[]>([]);
    const [loading, setLoading] = useState(false);
    const [sendingCommand, setSendingCommand] = useState<number | null>(null);

    const loadDevices = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(ENDPOINTS.GET_DEVICES, { withCredentials: true });
            const devList: DeviceItem[] = Array.isArray(res.data?.data)
                ? res.data.data
                : Array.isArray(res.data)
                    ? res.data
                    : [];

            // Only show devices whose DeviceType marks them as a vehicle —
            // i.e. they have a linked vehicleProfile or vehicleCapabilities JSON set.
            // The commandProtocol field is intentionally excluded because it defaults
            // to 'oscar' for all device types and doesn't indicate vehicle status.
            const vehicles = devList.filter(isVehicleDevice);

            // Enrich each vehicle with its latest command count
            const enriched: DeviceWithFleet[] = await Promise.all(
                vehicles.map(async (d) => {
                    try {
                        const cmdRes = await axios.get(ENDPOINTS.DEVICE_COMMANDS, {
                            withCredentials: true,
                            params: { deviceId: d.id, status: 'queued' },
                        });
                        const cmds: DeviceCommandItem[] = Array.isArray(cmdRes.data?.data) ? cmdRes.data.data : Array.isArray(cmdRes.data) ? cmdRes.data : [];
                        return {
                            ...d,
                            pendingCommands: cmds.length,
                            latestCommand: cmds[0] || null,
                        };
                    } catch {
                        return { ...d, pendingCommands: 0, latestCommand: null };
                    }
                })
            );
            setDevices(enriched);
        } catch (err) {
            console.error('Failed to load fleet:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void loadDevices();
        const interval = setInterval(loadDevices, 15000);
        return () => clearInterval(interval);
    }, [loadDevices]);

    const sendQuickCommand = async (deviceId: number, commandType: string) => {
        setSendingCommand(deviceId);
        try {
            await axios.post(ENDPOINTS.DEVICE_COMMANDS, {
                deviceId,
                commandType,
            }, { withCredentials: true });
            message.success(`${commandType} sent to device #${deviceId}`);
            void loadDevices();
        } catch (err: any) {
            message.error(err?.response?.data?.message || 'Failed to send command');
        } finally {
            setSendingCommand(null);
        }
    };

    const isOnline = (device: DeviceItem): boolean => {
        if (!device.lastHeartbeat) return false;
        const diff = Date.now() - new Date(device.lastHeartbeat).getTime();
        return diff < 60000;
    };

    const handleCardClick = (device: DeviceWithFleet) => {
        navigate(`/operations/device/${device.id}`);
    };

    if (loading && devices.length === 0) {
        return <Spin style={{ display: 'block', textAlign: 'center', padding: 40 }} />;
    }

    if (devices.length === 0) {
        return <Empty description="No devices assigned to your company yet — assign a device from the Devices page or Back Office" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    }

    return (
        <div>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text type="secondary">{devices.length} vehicle{devices.length !== 1 ? 's' : ''} in fleet</Text>
                <Button icon={<ReloadOutlined />} size="small" onClick={loadDevices} loading={loading}>
                    Refresh
                </Button>
            </div>
            <Row gutter={[12, 12]}>
                {devices.map((device) => {
                    const online = isOnline(device);
                    const snapshot = getStatusSnapshot(device);
                    const unitStatus = getUnitStatus(device);
                    const battery = getBatteryPercent(snapshot);
                    const moduleRows = getModuleRuntimeRows(snapshot, device.modules);
                    return (
                        <Col xs={24} sm={12} lg={8} xl={6} key={device.id}>
                            <Card
                                size="small"
                                className={`fleet-device-card ${online ? 'fleet-online' : 'fleet-offline'}`}
                                hoverable
                                onClick={() => handleCardClick(device)}
                                title={
                                    <Space>
                                        <Badge status={online ? 'success' : 'default'} />
                                        <RobotOutlined />
                                        <Text strong style={{ fontSize: 13 }}>{device.label}</Text>
                                    </Space>
                                }
                                extra={
                                    <Tag color={online ? 'green' : 'default'}>
                                        {online ? 'Online' : 'Offline'}
                                    </Tag>
                                }
                            >
                                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                                    <Space size={4} wrap>
                                        {device.deviceType && (
                                            <Text type="secondary" style={{ fontSize: 11 }}>
                                                {device.deviceType.name}
                                            </Text>
                                        )}
                                        {(device.deviceType?.vehicleProfile?.vehicleClass || device.deviceType?.vehicleCapabilities?.vehicleClass) && (
                                            <Tag
                                                color="blue"
                                                style={{ fontSize: 10, lineHeight: '16px', margin: 0 }}
                                            >
                                                {device.deviceType?.vehicleProfile?.vehicleClass || device.deviceType?.vehicleCapabilities?.vehicleClass}
                                            </Tag>
                                        )}
                                    </Space>
                                    {unitStatus && (
                                        <Text style={{ fontSize: 11 }}>
                                            <ThunderboltOutlined /> Status: {unitStatus}
                                        </Text>
                                    )}
                                    {battery !== undefined && (
                                        <Text style={{ fontSize: 11 }}>
                                            Battery: {battery.toFixed(0)}%
                                        </Text>
                                    )}
                                    {moduleRows.length > 0 && (
                                        <Text type="secondary" style={{ fontSize: 10 }}>
                                            Modules: {moduleRows.filter((row) => row.status === 'online' || row.status === 'ready' || row.status === 'completed').length}/{moduleRows.length} ready
                                        </Text>
                                    )}
                                    {device.lastHeartbeat && (
                                        <Text type="secondary" style={{ fontSize: 10 }}>
                                            <WifiOutlined /> Last seen: {new Date(device.lastHeartbeat).toLocaleTimeString()}
                                        </Text>
                                    )}
                                    {device.pendingCommands !== undefined && device.pendingCommands > 0 && (
                                        <Tag color="orange" style={{ fontSize: 10 }}>
                                            {device.pendingCommands} queued command{device.pendingCommands !== 1 ? 's' : ''}
                                        </Tag>
                                    )}
                                    {device.latestCommand && (
                                        <Text style={{ fontSize: 10 }}>
                                            <SendOutlined /> Next: <Tag color={commandStatusColor(device.latestCommand.status)} style={{ fontSize: 9 }}>
                                                {device.latestCommand.commandType}
                                            </Tag>
                                        </Text>
                                    )}

                                    <Space size={4} style={{ marginTop: 4 }} wrap>
                                        <Button
                                            size="small"
                                            type="primary"
                                            ghost
                                            icon={<AimOutlined />}
                                            loading={sendingCommand === device.id}
                                            onClick={(e) => { e.stopPropagation(); sendQuickCommand(device.id, 'return_home'); }}
                                            style={{ fontSize: 10, height: 22, padding: '0 6px' }}
                                        >
                                            Home
                                        </Button>
                                        <Button
                                            size="small"
                                            danger
                                            ghost
                                            loading={sendingCommand === device.id}
                                            onClick={(e) => { e.stopPropagation(); sendQuickCommand(device.id, 'emergency_stop'); }}
                                            style={{ fontSize: 10, height: 22, padding: '0 6px' }}
                                        >
                                            E-Stop
                                        </Button>
                                    </Space>
                                </Space>
                            </Card>
                        </Col>
                    );
                })}
            </Row>
        </div>
    );
};

export default DeviceFleetPanel;
